import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ProductService } from '@core/product.service';
import { QuestService } from '@core/services/quest/quest.service';
import { ApiResponse } from '@models/api-response';
import {
  AssessmentManualScoreUnitedResponse,
  AssessmentQuestionType,
  DeleteScoresConfirmationResponse,
  Standard
} from '@models/score';
import { FluencyAudioService } from '@services/fluency-audio/fluency-audio.service';
import {
  AssessmentStatus
} from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import { GameType } from '@shared/enums/game-type';
import { ProductType } from '@shared/enums/product-type';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { TopicType } from '@shared/enums/topic-type';
import { Helpers } from '@shared/helpers';
import { MarkdownService } from '@shared/markdown.service';
import {
  DeleteScoresConfirmationParams,
  IAssessmentManualScoreParams,
  IAssessmentRows,
  IBuildActivityRowsParams,
  IBuildAssessmentRowsParams,
  IScoreDetailBuildTableParams,
  IScoreDetailViewModel,
  ISetManualScoreParams
} from '@shared/modals/score-detail-modal/score-detail-params';

import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { map, Observable } from 'rxjs';
import AUDIO_PLAYER_CELL = ZbpDataTable.AUDIO_PLAYER_CELL;
import BLANK_COLUMN = ZbpDataTable.BLANK_COLUMN;
import Cell = ZbpDataTable.Cell;
import COLLAPSIBLE_CELL = ZbpDataTable.COLLAPSIBLE_CELL;
import COLLAPSIBLE_DEFINITION_LIST_TYPE = ZbpDataTable.COLLAPSIBLE_DEFINITION_LIST_TYPE;
import COLLAPSIBLE_IMAGE_TYPE = ZbpDataTable.COLLAPSIBLE_IMAGE_TYPE;
import COLLAPSIBLE_TABLE_TYPE = ZbpDataTable.COLLAPSIBLE_TABLE_TYPE;
import HTML_CELL = ZbpDataTable.HTML_CELL;
import ICON_CELL = ZbpDataTable.ICON_CELL;
import LINK_CELL = ZbpDataTable.LINK_CELL;
import NUMERIC_CELL = ZbpDataTable.NUMERIC_CELL;
import SCORE_BENCHMARK_CELL = ZbpDataTable.SCORE_BENCHMARK_CELL;
import SIMPLE_COLUMN = ZbpDataTable.SIMPLE_COLUMN;
import TEXT_CELL = ZbpDataTable.TEXT_CELL;
import TEXT_LIST_CELL = ZbpDataTable.TEXT_LIST_CELL;

@Injectable({ providedIn: 'root' })
export class ScoreDetailService {
  private readonly iconLabelTranslationKeys = [
    'shared-score-detail-icon-incorrect-label',
    'shared-score-detail-icon-correct-label',
  ];

  private readonly specialCaseSpellingGames = [
    GameType.UncontrolledSpellingWordLadders,
    GameType.ControlledSpellingWordLadders,
    GameType.ControlledSpelling1,
    GameType.ControlledSpelling2,
    GameType.ControlledSpelling3,
    GameType.ControlledSpelling4,
    GameType.UncontrolledSpelling,
    GameType.DragDropIntoBuckets,
    GameType.DragDropIntoSentences,
    GameType.TextEvidence,
  ] as const;

  private readonly hasFullKeyboardInputGameTypes = [
    GameType.UncontrolledSpelling,
    GameType.ControlledSpellingWordLadders
  ] as const;

  constructor(
    private markdownService: MarkdownService,
    private questService: QuestService,
    private productService: ProductService,
    private fluencyAudioService: FluencyAudioService,
  ) {
  }

  showAdditionalDetails(topicType: TopicType, parts: any[] | undefined): boolean {
    return [
      TopicType.FreeWrite,
      TopicType.CustomFreeWrite,
      TopicType.Game,
      TopicType.Practice,
      TopicType.PatternActivity,
      TopicType.WordSort,
      TopicType.Book,
    ].includes(topicType)
      || !!(parts)
      || !!(topicType === TopicType.Assessment && this.hasManualFluencyParts(parts));
  }

  private hasManualFluencyParts(parts: any[]): boolean {
    return parts && parts.some(
      part => part.questions && part.questions.some(
        question => question.assessmentQuestionType === AssessmentQuestionType.ManualFluency)
    );
  }

  buildTable(params: IScoreDetailBuildTableParams): IScoreDetailViewModel {
    const showAdditionalDetails = this.showAdditionalDetails(params.topicType, params.parts);
    const showTimeSpent = !!params.timeSpent;

    if (!params.scoreDetail && !params.parts) {
      return {
        columns: this.buildColumns(showAdditionalDetails, showTimeSpent),
        rows: [],
        hasUnscoredManualSection: false
      };
    }

    if (params.parts) {
      const { rows, hasUnscoredManualSection, formGroups } = this.buildAssessmentRows({
        parts: params.parts,
        manualScoreResponse: params.manualScoreResponse,
        isEditing: params.isEditing,
        showAdditionalDetails,
        productType: params.productType,
      });

      return {
        columns: this.buildColumns(showAdditionalDetails, showTimeSpent),
        rows,
        hasUnscoredManualSection,
        formGroups,
      };
    }

    return {
      columns: this.buildColumns(showAdditionalDetails, showTimeSpent),
      rows: this.buildActivityRows({
        topicType: params.topicType,
        score: params.score,
        benchmark: params.benchmark,
        scoreDetail: params.scoreDetail,
        timeSpent: params.timeSpent,
        showAdditionalDetails,
      }),
      hasUnscoredManualSection: false,
    };
  }

  buildColumns(showAdditionalDetails: boolean, showTimeSpent: boolean): ZbpDataTable.Column[] {
    const newColumns: ZbpDataTable.Column[] = [
      {
        type: SIMPLE_COLUMN,
        key: 'part',
        headerTranslationKey: 'shared-score-detail-column-part'
      },
      {
        type: SIMPLE_COLUMN,
        key: 'objective',
        headerTranslationKey: 'shared-score-detail-column-objective'
      },
      {
        type: SIMPLE_COLUMN,
        key: 'score',
        headerTranslationKey: 'shared-score-detail-column-score'
      },
      {
        type: SIMPLE_COLUMN,
        key: 'benchmark',
        headerTranslationKey: 'shared-score-detail-column-benchmark'
      },
    ];

    if (showTimeSpent) {
      // prepends to the start of the array
      newColumns.unshift({
        type: SIMPLE_COLUMN,
        key: 'timeSpent',
        headerTranslationKey: 'shared-score-detail-column-time-spent'
      });
    }

    if (showAdditionalDetails) {
      newColumns.push({
        type: BLANK_COLUMN,
        key: 'collapsible',
        ariaLabelTranslationKey: 'shared-score-detail-column-collapsible-label'
      });
    }

    return newColumns;
  }

  getAssessmentManualScore(request: IAssessmentManualScoreParams): Observable<AssessmentManualScoreUnitedResponse> {
    return this.questService.getAssessmentManualScore({
      assessmentKey: request.assessmentKey,
      productType: request.productType,
      gradeType: Helpers.mapVariantToGradeType(request.variant) as any,
      classroomId: request.classroomId,
      studentId: request.studentId,
    }).pipe(
      map((res) => {
        if (res.success) {
          return res.response;
        }
        throw new Error(res.messages[0] || 'Unable to load Assessment Manual Score');
      }),
    );
  }

  setManualScore(request: ISetManualScoreParams): Observable<any> {
    return this.questService.setAssessmentManualScore({
      assessmentKey: request.assessmentKey,
      productType: request.productType,
      gradeType: Helpers.mapVariantToGradeType(request.variant) as any,
      classroomId: request.classroomId,
      studentId: request.studentId,
      data: request.data,
    }).pipe(
      map((res) => {
        if (res.success) {
          return res;
        }
        throw new Error(res.messages[0] || 'Unable to save Assessment Manual Score');
      }),
    );
  }

  getDeleteScoresConfirmation(
    params: DeleteScoresConfirmationParams
  ): Observable<ApiResponse<DeleteScoresConfirmationResponse[]>> {
    return this.questService.getDeleteScoresConfirmation(params).pipe(
      map((res) => {
        if (res.success) {
          return res;
        }
        throw new Error(res.messages[0] || 'Unable to retrieve assessment history');
      }),
    );
  }

  deleteCurrentScore(activityId: string): Observable<any> {
    return this.questService.deleteCurrentScore(activityId).pipe(
      map((res) => {
        if (res.success) {
          return res.response;
        }
        throw new Error(res.messages[0] || 'Unable to delete current score');
      }),
    );
  }

  isEditableStatus(status: AssessmentStatus | undefined): boolean {
    return status === AssessmentStatus.Completed
      || status === AssessmentStatus.AddScore
      || status === AssessmentStatus.PartiallyScored
      || status === AssessmentStatus.ReadyForScoring;
  }

  /** If a manualResponse is included in params, the parts used will be from the manual score. */
  private buildAssessmentRows(params: IBuildAssessmentRowsParams): IAssessmentRows {
    const rows: ZbpDataTable.Row[] = [];
    const formGroups: FormGroup[] = [];
    let hasUnscoredManualSection = false;

    /**
     * If a manual score was retrieved for the parts, then use those parts.
     *
     * Parts are not always shown (e.g. Print All Score Details modal, so they're not always retrieved)
     */
    const partsToUse = params.manualScoreResponse ? params.manualScoreResponse.parts : params.parts;

    partsToUse.forEach((part) => {
      const isUnscoredManualPart = part.isManualSection && part.benchmark === ScoreBenchmark.NotScored;
      hasUnscoredManualSection = hasUnscoredManualSection || isUnscoredManualPart;

      const newCells: Record<string, ZbpDataTable.Cell> = {
        part: { type: TEXT_CELL, text: part.part !== '-1' ? part.part : '-' },
        objective: { type: TEXT_CELL, text: part.objective ?? '-' },
      };

      // Create form controls for editable cells
      let scoreControl: FormControl | null = null;
      if (params.isEditing || isUnscoredManualPart) {
        scoreControl = new FormControl(
          part.benchmark === ScoreBenchmark.NotScored ? null : part.totalCorrect,
          [Validators.min(0), Validators.max(part.totalQuestions ?? 0)]
        );
        const partControl = new FormControl(part.part);
        const group = new FormGroup({ part: partControl, totalCorrect: scoreControl });
        formGroups.push(group);

        newCells.score = {
          type: NUMERIC_CELL,
          maxValue: part.totalQuestions,
          formControl: scoreControl,
          showMax: true,
          constrainToLimits: true,
          placeholder: '-',
          allowMinusToNull: true,
          allowNullValue: true,
          applyConstraintOnNullInput: false,
          ariaLabel: part.objective ? `${part.objective} score` : 'score',
          subtractButtonArialabel: 'minus, click to reduce score',
          addButtonArialabel: 'plus, click to increase score',
        };
      } else {
        newCells.score = {
          type: TEXT_CELL,
          text: part.benchmark === ScoreBenchmark.NotScored ? `-/${part.totalQuestions}` : part.score,
        };
      }

      newCells.benchmark = { type: SCORE_BENCHMARK_CELL, scoreBenchmark: part.benchmark };
      if (params.showAdditionalDetails) {
        newCells.collapsible = this.buildCollapsibleRowSection(params, part);
      }

      rows.push({ cells: newCells });
    });

    return { rows, hasUnscoredManualSection, formGroups };
  }

  private buildCollapsibleRowSection(params: IBuildAssessmentRowsParams, part: any): Cell {
    const partToShow = params.parts.find(x => x.part === part.part);
    const { fluencyQuestions, standardQuestions } = this.separateFluencyAndStandardQuestions(partToShow);

    const collapsibleSection: Cell = {
      type: COLLAPSIBLE_CELL,
      content: [],
    };

    if (part.benchmark === ScoreBenchmark.NotScored) {
      // do not show any additional details for unscored sections, as they are not scored yet
      return { type: TEXT_CELL, text: '' };
    }
  
    const onlineFluencyQuestions = fluencyQuestions.filter(question => !!question.attemptLink);
    if (onlineFluencyQuestions.length > 0) {
      const hasStandards = this.hasStandards(onlineFluencyQuestions);
      collapsibleSection.content.push({
        type: COLLAPSIBLE_TABLE_TYPE,
        columns: this.getFluencyCollapsibleColumns(hasStandards),
        rows: this.buildFluencyRows(part, onlineFluencyQuestions, hasStandards),
      });
    }
    if (standardQuestions.length > 0) {
      // standard questions do not display for manual sections, nor while the teacher edits the score
      const hasStandards = this.hasStandards(standardQuestions);
      collapsibleSection.content.push({
        type: COLLAPSIBLE_TABLE_TYPE,
        columns: this.getCollapsibleColumns(hasStandards),
        rows: this.buildAssessmentQuestionRows(standardQuestions, params.productType, hasStandards),
      });
    }
    if (collapsibleSection.content.length === 0) {
      // if we end up not adding any content, return a blank cell instead
      return { type: TEXT_CELL, text: '' };
    }
    return collapsibleSection;
  }

  private hasStandards(questions: any[]): boolean {
    return questions.some(question => question.questionStandards?.length > 0);
  }

  private buildFluencyRows(part: any, questions: any[], hasStandards: boolean): ZbpDataTable.Row[] {
    return questions.map((question) => {
      const newCells: Record<string, ZbpDataTable.Cell> = {};

      if (hasStandards) {
        newCells.standards = this.buildStandardsCell(question);
      }

      newCells.prompt = {
        type: LINK_CELL,
        textTranslationKey: 'shared-score-detail-prompt-scoring-link',
        role: 'link',
        onClick: () => window.open(part.manualTestLocation, '_blank', 'noopener'),
      };

      newCells.audio = {
        type: AUDIO_PLAYER_CELL,
        playerTitle: 'Audio',
        assetName: 'Fluency',
        audioFile: this.fluencyAudioService.getFluencyAudioFile(question.attemptLink),
      };

      return { cells: newCells };
    });
  }

  private buildStandardsList(question: any): ZbpDataTable.DefinitionListItem[] | undefined {
    const standards: Standard[] = question.questionStandards ?? [];
    if (standards.length === 0) {
      return undefined;
    }

    return standards.map(standard => ({
      term: standard.standardCode,
      description: standard.standardDescription,
    }));
  }

  private buildStandardsCell(question: any): Cell {
    const standards = this.buildStandardsList(question);
    if (!standards) {
      return { type: TEXT_CELL, text: '-' };
    }

    return {
      type: COLLAPSIBLE_CELL,
      textList: standards.map(standard => standard.term),
      content: [{
        type: COLLAPSIBLE_DEFINITION_LIST_TYPE,
        items: standards,
        definitionNotAvailableTranslationKey: 'shared-score-detail-collapsible-definition-not-available',
      }],
    };
  }

  private separateFluencyAndStandardQuestions(part: any): { fluencyQuestions: any[], standardQuestions: any[] } {
    const result = { fluencyQuestions: [], standardQuestions: [] };
    const questions = part?.questions;
    if (questions && Array.isArray(questions)) {
      questions.forEach((question) => {
        if (question.assessmentQuestionType === AssessmentQuestionType.ManualFluency) {
          result.fluencyQuestions.push(question);
        } else {
          result.standardQuestions.push(question);
        }
      });
    }
    return result;
  }

  private buildAssessmentQuestionRows(
    questions: any[],
    productType?: ProductType,
    hasStandards: boolean = false,
  ): ZbpDataTable.Row[] {
    const newRows: ZbpDataTable.Row[] = [];
    questions.forEach((item) => {
      const newCells: Record<string, ZbpDataTable.Cell> = {
        ...(hasStandards ? { standards: this.buildStandardsCell(item) } : {}),
        prompt: { type: HTML_CELL, html: this.getProgressPrompt(item, productType) },
        answer: { type: TEXT_CELL, text: this.getProgressAnswer(item, productType) },
        choices: item.answerChoices?.length
          ? { type: TEXT_LIST_CELL, textList: item.answerChoices }
          : { type: TEXT_CELL, text: '-' },
        correct: this.buildIsCorrectIconCell(item.correct),
      };
      newRows.push({ cells: newCells });
    });
    return newRows;
  }

  private buildActivityRows(params: IBuildActivityRowsParams): ZbpDataTable.Row[] {
    const newCells: {
      timeSpent?: ZbpDataTable.TextCell,
      part: ZbpDataTable.TextCell,
      objective: ZbpDataTable.TextCell,
      score: ZbpDataTable.TextCell,
      benchmark: ZbpDataTable.ScoreBenchmarkCell,
      collapsible?: ZbpDataTable.CollapsibleCell,
    } = {
      part: { type: TEXT_CELL, text: '-' },
      objective: { type: TEXT_CELL, text: '-' },
      score: { type: TEXT_CELL, text: params.score },
      benchmark: { type: SCORE_BENCHMARK_CELL, scoreBenchmark: params.benchmark },
    };

    if (params.timeSpent) {
      newCells.timeSpent = { type: TEXT_CELL, text: params.timeSpent };
    }

    if (params.showAdditionalDetails) {
      newCells.collapsible = { type: COLLAPSIBLE_CELL, content: [] };
    }

    if (params.topicType === TopicType.FreeWrite
      || params.topicType === TopicType.CustomFreeWrite) {
      newCells.collapsible?.content?.push(
        {
          type: COLLAPSIBLE_IMAGE_TYPE,
          src: params.scoreDetail.snapshotAttemptImagePath
        }
      );
    } else if (params.topicType === TopicType.Game
      && params.scoreDetail.gameType === GameType.MultipleChoice) {
      newCells.collapsible?.content?.push({
        type: COLLAPSIBLE_TABLE_TYPE,
        columns: this.getCollapsibleColumns(),
        rows: this.getMultipleChoiceRow(params.scoreDetail),
      });
    } else if (params.topicType === TopicType.Game
      && this.specialCaseSpellingGames.findIndex(g => g === params.scoreDetail.gameType) > -1) {
      newCells.collapsible?.content?.push({
        type: COLLAPSIBLE_TABLE_TYPE,
        columns: this.getCollapsibleColumns(),
        rows: this.getSpecialCaseSpellingGameRows(params.scoreDetail),
      });
    } else if (params.topicType === TopicType.Game
      || params.topicType === TopicType.Practice
      || params.topicType === TopicType.PatternActivity) {
      newCells.collapsible?.content?.push({
        type: COLLAPSIBLE_TABLE_TYPE,
        columns: this.getCollapsibleColumns(),
        rows: this.getGameRows(params.scoreDetail.gameResult),
      });
    } else if (params.topicType === TopicType.WordSort) {
      newCells.collapsible?.content?.push({
        type: COLLAPSIBLE_TABLE_TYPE,
        columns: this.getCollapsibleColumns(),
        rows: this.getWordSortRows(params.scoreDetail.gameResult),
      });
    } else if (params.topicType === TopicType.Book) {
      // blank out score so it doesn't show duplicate of time spent.
      newCells.score.text = '-';
      newCells.collapsible?.content?.push({
        type: COLLAPSIBLE_TABLE_TYPE,
        columns: this.getBookCollapsibleColumns(),
        rows: this.getEbookRows(params?.scoreDetail),
      });
    }

    // Note TopicType.Trace does not show additional detail
    return [{ cells: newCells }];
  }

  private getStandardsColumn(): ZbpDataTable.Column {
    return {
      type: SIMPLE_COLUMN,
      key: 'standards',
      headerTranslationKey: 'shared-score-detail-collapsible-column-standards'
    };
  }

  private getFluencyCollapsibleColumns(hasStandards: boolean): ZbpDataTable.Column[] {
    let newColumns: ZbpDataTable.Column[] = [];

    if (hasStandards) {
      newColumns.push(this.getStandardsColumn());
    }

    newColumns = [
      ...newColumns,
      {
        type: SIMPLE_COLUMN,
        key: 'prompt',
        headerTranslationKey: 'shared-score-detail-collapsible-column-prompt-scoring'
      },
      {
        type: BLANK_COLUMN,
        key: 'audio',
        ariaLabelTranslationKey: 'shared-score-detail-collapsible-column-audio-label'
      },
    ];

    return newColumns;
  }

  private getCollapsibleColumns(hasStandards: boolean = false): ZbpDataTable.Column[] {
    let newColumns: ZbpDataTable.Column[] = [];

    if (hasStandards) {
      newColumns.unshift(this.getStandardsColumn());
    }
    newColumns = [
      ...newColumns,
      {
        type: SIMPLE_COLUMN,
        key: 'prompt',
        headerTranslationKey: 'shared-score-detail-collapsible-column-prompt'
      },
      {
        type: SIMPLE_COLUMN,
        key: 'answer',
        headerTranslationKey: 'shared-score-detail-collapsible-column-answer'
      },
      {
        type: SIMPLE_COLUMN,
        key: 'choices',
        headerTranslationKey: 'shared-score-detail-collapsible-column-choices'
      },
      {
        type: SIMPLE_COLUMN,
        key: 'correct',
        headerTranslationKey: 'shared-score-detail-collapsible-column-correct'
      },
    ];

    return newColumns;
  }

  private getBookCollapsibleColumns(): ZbpDataTable.Column[] {
    return [
      {
        type: SIMPLE_COLUMN,
        key: 'page',
        headerTranslationKey: 'shared-score-detail-collapsible-column-page'
      },
      {
        type: SIMPLE_COLUMN,
        key: 'time',
        headerTranslationKey: 'shared-score-detail-collapsible-column-time'
      },
    ];
  }

  private buildIsCorrectIconCell(isCorrect: boolean): ZbpDataTable.Cell {
    return {
      type: ICON_CELL,
      iconName: isCorrect ? zbpIconNames.check_mark : zbpIconNames.x_cross,
      iconSize: 'font-size',
      primaryColor: isCorrect ? 'var(--active-green)' : 'var(--brand-black)',
      arialLabelTranslationKey: this.iconLabelTranslationKeys[Number(isCorrect)],
    };
  }

  private getGameRows(gameResult: any): ZbpDataTable.Row[] {
    const newRows: ZbpDataTable.Row[] = [];

    if (gameResult.completionResult) {
      gameResult.completionResult.completionResults.forEach((item) => {
        const newCells: Record<string, ZbpDataTable.Cell> = {
          prompt: { type: TEXT_CELL, text: item.word ?? '-' },
          answer: { type: HTML_CELL, html: item.spellingCorrect ? item.word : item.spellingAttempt },
          choices: { type: TEXT_CELL, text: 'Full Keyboard' },
          correct: this.buildIsCorrectIconCell(item.spellingCorrect),
        };
        newRows.push({ cells: newCells });
      });

      gameResult.completionResult.completionResults.forEach((item) => {
        const newCells: Record<string, ZbpDataTable.Cell> = {
          prompt: { type: TEXT_CELL, text: item.sentence ?? '-' },
          answer: { type: TEXT_CELL, text: '-' },
          choices: item.answerChoices?.length
            ? { type: TEXT_LIST_CELL, textList: item.answerChoices }
            : { type: TEXT_CELL, text: '-' },
          correct: this.buildIsCorrectIconCell(item.sortCorrect),
        };
        newRows.push({ cells: newCells });
      });
    }

    if (gameResult.letterResults?.length > 0) {
      gameResult.letterResults.forEach((item) => {
        const isCorrect = !item.letterDetailResults.some(x => x.correct === false);
        const newCells: Record<string, ZbpDataTable.Cell> = {
          prompt: { type: TEXT_CELL, text: '-' },
          answer: { type: TEXT_CELL, text: item.word ?? '-' },
          choices: item.answerChoices?.length
            ? { type: TEXT_LIST_CELL, textList: item.answerChoices }
            : { type: TEXT_CELL, text: '-' },
          correct: this.buildIsCorrectIconCell(isCorrect),
        };
        newRows.push({ cells: newCells });
      });
    }

    if (gameResult.wordAttemptCountResults?.length > 0) {
      gameResult.wordAttemptCountResults.forEach((item) => {
        const newCells: Record<string, ZbpDataTable.Cell> = {
          prompt: { type: TEXT_CELL, text: '-' },
          answer: { type: TEXT_CELL, text: item.word ?? '-' },
          choices: item.answerChoices?.length
            ? { type: TEXT_LIST_CELL, textList: item.answerChoices }
            : { type: TEXT_CELL, text: '-' },
          correct: this.buildIsCorrectIconCell(item.attempts === 1),
        };
        newRows.push({ cells: newCells });
      });
    }

    const results = gameResult.fillInTheBlankResults.concat(
      gameResult.highlightResults,
      gameResult.identifyResults,
      gameResult.rewriteResults,
      gameResult.sentenceManipulationResults,
      gameResult.multipleChoiceResults,
      gameResult.wordAttemptResults,
      gameResult.patternMatchingResults,
    );

    results.filter(item => item !== undefined).forEach((item) => {
      const newCells: Record<string, ZbpDataTable.Cell> = {
        prompt: { type: TEXT_CELL, text: item.sentence ?? '-' },
        answer: { type: TEXT_CELL, text: item.answer ?? item.word ?? '-' },
        choices: item.answerChoices?.length
          ? { type: TEXT_LIST_CELL, textList: item.answerChoices }
          : { type: TEXT_CELL, text: '-' },
        correct: this.buildIsCorrectIconCell(item.correct),
      };
      newRows.push({ cells: newCells });
    });

    return newRows;
  }

  private getSpecialCaseSpellingGameRows(scoreDetail: any): ZbpDataTable.Row[] {
    const newRows: ZbpDataTable.Row[] = [];

    if (scoreDetail.gameType === GameType.DragDropIntoSentences) {
      scoreDetail.scores.forEach((item) => {
        const isCorrect = item.answerAttempts.every(answer => answer.correct === true);
        const newCells: Record<string, ZbpDataTable.Cell> = {
          prompt: { type: TEXT_CELL, text: item.completedSentence ?? '-' },
          answer: { type: TEXT_CELL, text: '-' },
          choices: { type: TEXT_CELL, text: '-' },
          correct: this.buildIsCorrectIconCell(isCorrect),
        };
        newRows.push({ cells: newCells });
      });
    } else if (scoreDetail.gameType === GameType.DragDropIntoBuckets) {
      scoreDetail.scores.forEach((item) => {
        item.answerAttempts.forEach((answerItem) => {
          const isCorrect = answerItem.correctAnswer === answerItem.answerAttempt;
          const newCells: Record<string, ZbpDataTable.Cell> = {
            prompt: { type: TEXT_CELL, text: `${answerItem.correctAnswer} (_${item.bucketLabel})` },
            answer: { type: TEXT_CELL, text: '-' },
            choices: { type: TEXT_CELL, text: '-' },
            correct: this.buildIsCorrectIconCell(isCorrect),
          };
          newRows.push({ cells: newCells });
        });
      });
    } else if (scoreDetail.gameType === GameType.ControlledSpelling3
      || scoreDetail.gameType === GameType.TextEvidence) {
      scoreDetail.scores.forEach((item) => {
        const newCells: Record<string, ZbpDataTable.Cell> = {
          prompt: { type: TEXT_CELL, text: item.prompt ? item.prompt : '-' },
          answer: { type: TEXT_CELL, text: item.studentAnswer ? item.studentAnswer : '-' },
          choices: { type: TEXT_CELL, text: item.choices ? item.choices : '-' },
          correct: this.buildIsCorrectIconCell(!!item.isCorrect),
        };
        newRows.push({ cells: newCells });
      });
    } else {
      scoreDetail.scores.forEach((item) => {
        const isCorrect = item.answer === item.attempt;
        const newCells: Record<string, ZbpDataTable.Cell> = {
          prompt: { type: TEXT_CELL, text: item.answer ?? '-' },
          answer: { type: TEXT_CELL, text: item.attempt ?? '-' },
          choices: {
            type: TEXT_CELL,
            text: this.hasFullKeyboardInputGameTypes.includes(scoreDetail.gameType) ? 'Full Keyboard' : '-',
          },
          correct: this.buildIsCorrectIconCell(isCorrect),
        };
        newRows.push({ cells: newCells });
      });
    }

    return newRows;
  }

  private getMultipleChoiceRow(scoreDetail: any): ZbpDataTable.Row[] {
    const newRows: ZbpDataTable.Row[] = [];
    scoreDetail.scores.forEach((item) => {
      const isCorrect = item.correctAnswers.includes(item.answerAttempt);
      const newCells: Record<string, ZbpDataTable.Cell> = {
        prompt: { type: HTML_CELL, html: item.promptText ? item.promptText : '-' },
        answer: { type: TEXT_CELL, text: item.answerAttempt ? item.answerAttempt : '-' },
        choices: { type: TEXT_CELL, text: item.correctAnswers },
        correct: this.buildIsCorrectIconCell(isCorrect),
      };
      newRows.push({ cells: newCells });
    });
    return newRows;
  }

  private getWordSortRows(gameResult: any): ZbpDataTable.Row[] {
    const newRows: ZbpDataTable.Row[] = [];
    gameResult.wordSortResults.forEach((item) => {
      const newCells: Record<string, ZbpDataTable.Cell> = {
        prompt: { type: HTML_CELL, lineHeight: '1.3rem', html: item.targetGroup ?? '-' },
        answer: { type: TEXT_CELL, text: item.word ?? '-' },
        choices: { type: TEXT_CELL, text: 'Full Keyboard' },
        correct: this.buildIsCorrectIconCell(item.correct),
      };
      newRows.push({ cells: newCells });
    });
    return newRows;
  }

  private getEbookRows(scoreDetail: any[]): ZbpDataTable.Row[] {
    const newRows: ZbpDataTable.Row[] = [];
    scoreDetail?.forEach((item) => {
      const newCells: Record<string, ZbpDataTable.Cell> = {
        page: { type: TEXT_CELL, text: item.pageName ? item.pageName : '-' },
        time: { type: TEXT_CELL, text: item.duration ? item.duration : '-' },
      };
      newRows.push({ cells: newCells });
    });
    return newRows;
  }

  private getProgressPrompt(item: any, productType?: ProductType): string {
    if (productType && this.productService.superkidsProducts.includes(productType)) {
      return item.instructions;
    }

    if (productType && this.productService.gumProducts.includes(productType)) {
      return this.markdownService.render(item.prompt);
    }

    if (productType && this.productService.spellingProducts.includes(productType)) {
      return item.correctValue;
    }

    return '-';
  }

  private getProgressAnswer(item: any, productType?: ProductType): string {
    if (productType && this.productService.superkidsProducts.includes(productType)) {
      return item.attempts?.slice(-1)[0]?.attempt?.slice(-1)[0] ?? '-';
    }

    if (productType && this.productService.gumProducts.includes(productType)) {
      return item.attempts?.slice(-1)[0]?.attempt?.slice(-1)[0] ?? '-';
    }

    if (productType && this.productService.spellingProducts.includes(productType)) {
      return item.prompt;
    }

    return '-';
  }
}
